/**
 * ReadinessView -- the SAMA OpsRes maturity engine surface (P267.4).
 *
 * Route: /readiness
 *
 * The 10 weighted dimensions with their live evidence read + suggested
 * score, the weighted index + A-E tier (floor rule surfaced), and the
 * dual-signed self-assessment ceremony (start -> score each dimension ->
 * CRO + Head of OpsRes sign-off). Admin curates the draft; the two
 * sign-offs gate on realm role when configured (admin-only by default).
 *
 * Hardcoded English to match the sibling shared/ exec views.
 */
import { useTranslation } from "react-i18next";
import { stateLabel } from "shared/vocab/workflowState";
import type { TFunction } from "i18next";
import { useState } from "react";
import { Bdi } from "shared/components/Bdi";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { ResilienceScoreCard } from "shared/readiness/ResilienceScoreCard";
import { canReadReadiness } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { canDownloadReadinessPack } from "shared/api/authority";
import type { Readiness } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";
import { seatHolder, useSeatHolders } from "shared/api/useSeatHolders";
import {
  downloadReadinessPack,
  useMaturityAssessment,
  useReadiness,
  useSetDimension,
  useSignoff,
  useStartAssessment,
} from "shared/readiness/useReadiness";

import { SignatureHistory } from "shared/components/SignatureHistory";
type SignRole = "cro" | "ops_res";

//: The realm role that owns each signature. Mirrors the map the
//: readiness router gates on; a test pins the two together.
const SIGN_REALM_ROLE: Record<SignRole, string> = {
  cro: "cro",
  ops_res: "head_of_opsres",
};

const TIER_TONE: Record<string, string> = {
  A: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  B: "bg-sky-100 text-sky-800 ring-sky-300",
  C: "bg-amber-100 text-amber-800 ring-amber-300",
  D: "bg-orange-100 text-orange-900 ring-orange-300",
  E: "bg-rose-100 text-rose-800 ring-rose-300",
};

const HORIZON_LABEL: Record<string, string> = {
  H1: "Horizon 1 (0-6m)",
  H2: "Horizon 2 (6-18m)",
  H3: "Horizon 3 (18m+)",
};

function Scorecard({
  index,
  tier,
  floorApplied,
  source,
}: {
  index: number | null;
  tier: string | null;
  floorApplied: boolean;
  source: string;
}) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="readiness-scorecard"
      className="rounded-lg border border-slate-200 bg-white p-5"
    >
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">
            {t("screens.readinessView.weightedMaturityIndex")}
          </div>
          <div
            data-testid="readiness-index"
            className="font-display text-4xl text-slate-900 tabular-nums"
          >
            {index !== null ? index.toFixed(2) : "—"}
          </div>
        </div>
        {tier && (
          <span
            data-testid="readiness-tier"
            data-tier={tier}
            className={`rounded px-3 py-1 text-lg font-semibold ring-1 ${
              TIER_TONE[tier] ?? TIER_TONE.E
            }`}
          >
            {t("screens.readinessView.tier")} {tier}
          </span>
        )}
        <span className="text-xs text-slate-500">{source}</span>
      </div>
      {floorApplied && (
        <p
          data-testid="readiness-floor-banner"
          className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("screens.readinessView.floorRuleAppliedTheTierIsCapped")}
        </p>
      )}
    </section>
  );
}

/**
 * Round 9 G -- one evidence probe, named in the reader's language.
 *
 * The backend appends a count to a present probe ("Board snapshot (3)")
 * and that suffix stays as composed: a figure is not a word, and
 * sending the count separately to recompose it here would be work for
 * no gain. So the label is translated and anything the backend added
 * after it is carried through.
 */
function probeLabel(key: string | undefined, composed: string, t: TFunction): string {
  if (!key) return composed;
  const name = t(`readiness.probe.${key}`, { defaultValue: "" });
  if (!name) return composed;
  const suffix = /\s(\(\d+\))$/.exec(composed);
  return suffix ? `${name} ${suffix[1]}` : name;
}

export function ReadinessView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  // Round 9 E9 -- the route, not the card. A seat that may not read
  // this got "Could not load the readiness view", which blames the
  // view for a decision the product made on purpose.
  const mayAsk = me.isSuccess && canReadReadiness(me.data);
  const read = useReadiness(mayAsk);
  const assessment = useMaturityAssessment(mayAsk);
  const start = useStartAssessment();
  const setDim = useSetDimension();
  const signoff = useSignoff();
  const [signNote, setSignNote] = useState("");
  const toast = useToast();
  // Round 17 G19 -- who the next leg waits on, by name.
  const seats = useSeatHolders(mayAsk);

  if (me.isSuccess && !mayAsk) {
    return (
      <p data-testid="readiness-forbidden" className="text-sm text-ink-2">
        {t("screens.readinessView.forbidden")}
      </p>
    );
  }

  if (read.isError) {
    return (
      <div data-testid="readiness-error" className="text-sm text-slate-500">
        {t("screens.readinessView.couldNotLoadTheReadinessView")}
      </div>
    );
  }
  if (!read.data) {
    return <div className="text-sm text-slate-500">…</div>;
  }
  const r: Readiness = read.data;
  const a = assessment.data ?? null;
  const scored = a ? Object.keys(a.dimensions).length : 0;
  const allScored = scored === r.dimensions.length;
  const isDraftEditable = a !== null && a.workflow_state !== "approved" && isAdmin;

  //: A5 -- which seat this session holds. The API admits the realm role
  //: and an administrator does NOT sign on the CRO's behalf, so offering
  //: the control on `is_admin` showed it to exactly the person it would
  //: refuse. Best-effort, as everywhere: the backend is the real gate,
  //: and it cannot tell us whether the environment has signing switched
  //: off (in which case an administrator signs instead).
  const realmRoles = me.data?.realm_roles ?? [];
  const holds = (role: SignRole) => realmRoles.includes(SIGN_REALM_ROLE[role]);
  const signerName = me.data?.display_name?.trim() || me.data?.username || "";

  const onSignoff = (role: SignRole, note: string) => {
    signoff.mutate(
      { role, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: (d) =>
          toast.push({
            tone: "success",
            message: t("screens.readinessView.signedToast", {
              next:
                d.workflow_state === "approved"
                  ? t("screens.readinessView.signedToastDone")
                  : (() => {
                      // Round 17 G19 -- the next signer by name where one
                      // person holds the seat.
                      const nextSeat = role === "cro" ? "head_of_opsres" : "cro";
                      const label = t(
                        `screens.readinessView.${role === "cro" ? "opsRes" : "cro"}`,
                      );
                      const h = seats.data?.holders?.[nextSeat] ?? [];
                      return h.length === 1
                        ? t("screens.readinessView.signedToastAwaitingNamed", {
                            who: h[0],
                            role: label,
                          })
                        : t("screens.readinessView.signedToastAwaiting", {
                            role: label,
                          });
                    })(),
            }),
          }),
        // The server's own sentence says why; it is written for a
        // reader (A1), so it is not replaced with a generic one.
        onError: (e) =>
          toast.push({
            tone: "error",
            message: e.message || t("screens.readinessView.signFailed"),
          }),
      },
    );
  };

  return (
    <article data-testid="readiness-view" className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="benchmark"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("screens.readinessView.resilienceReadiness")}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            {t("screens.readinessView.theBankAposSSamaOperationalResilience")}
          </p>
          <Link
            to="/readiness/basel"
            data-testid="readiness-basel-link"
            className="mt-1 inline-block text-sm text-brand-brassDark hover:underline"
          >
            {t("screens.readinessView.basel7OperationalResilienceReadinessCrosswal", {
              framework: "Basel-7",
            })}
          </Link>
        </div>
        {/* D3 -- this was offered to the Head of Operational
            Resilience and returned a silent 403. She reads every
            figure on the page and co-signs the assessment; what she
            cannot do is take the signed pack, which is an approver's
            artefact. Say that, rather than refusing after the click. */}
        {canDownloadReadinessPack(me.data) ? (
          <button
            type="button"
            data-testid="readiness-download-pack"
            onClick={() => {
              void downloadReadinessPack()
                .then(() =>
                  toast.push({
                    tone: "success",
                    message: t("screens.readinessView.packDownloaded"),
                  }),
                )
                .catch((e) =>
                  toast.push({ tone: "error", message: String(e?.message ?? e) }),
                );
            }}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10"
          >
            {t("screens.readinessView.downloadSignedPack")}
          </button>
        ) : (
          <p
            data-testid="readiness-pack-not-yours"
            className="max-w-xs text-xs text-slate-500"
          >
            {t("screens.readinessView.packIsApprovers")}
          </p>
        )}
      </header>

      <ResilienceScoreCard />

      <Scorecard
        index={a?.set_index ?? r.suggested_index}
        tier={a?.set_tier ?? r.tier_code}
        floorApplied={a?.floor_applied ?? r.floor_applied}
        source={
          /* Round 11 F3 -- "cro_signed" on the readiness scorecard.
             Composed English with our column value interpolated into
             it: the state has had a word in the catalogue since round
             2 and this sentence was built without asking for it. */
          a
            ? t("screens.readinessView.selfAssessed", {
                version: a.version,
                state: stateLabel(a.workflow_state),
              })
            : r.not_suggested_reason
              ? t("screens.readinessView.notSuggested")
              : t("screens.readinessView.suggestedFromEvidence")
        }
      />

      {/* Ceremony */}
      <section
        data-testid="readiness-ceremony"
        className="rounded-lg border border-slate-200 bg-white p-4"
      >
        {!a ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-600">
              {isAdmin
                ? t("screens.readinessView.noSelfAssessmentYetStartOneTo")
                : t("screens.readinessView.startAsk")}
            </p>
            {isAdmin && (
              <button
                type="button"
                data-testid="readiness-start"
                onClick={() => start.mutate()}
                disabled={start.isPending}
                className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1.5 text-sm font-semibold text-brand-navy disabled:opacity-50"
              >
                {start.isPending
                  ? t("screens.readinessView.starting")
                  : t("screens.readinessView.startAMaturityAssessment")}
              </button>
            )}
          </div>
        ) : a.workflow_state === "approved" ? (
          <div className="space-y-2">
            <p data-testid="readiness-approved" className="text-sm text-emerald-800">
              {t("screens.readinessView.approvedByBoard")}
            </p>
            {/* C1 -- two names run together in a sentence, and the
                comment each of them left beside their signature shown
                nowhere. This is the assessment a board signs; the
                reason it signed is the part a supervisor reads. */}
            <SignatureHistory
              testid="readiness-signatures"
              entries={[
                {
                  roleLabel: t("roles.cro"),
                  name: a.cro_signoff_name,
                  at: a.cro_signoff_at,
                  note: a.cro_signoff_note ?? null,
                },
                {
                  roleLabel: t("roles.ops_res"),
                  name: a.ops_res_signoff_name,
                  at: a.ops_res_signoff_at,
                  note: a.ops_res_signoff_note ?? null,
                },
              ]}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              <span className="font-semibold tabular-nums">
                {scored}/{r.dimensions.length}
              </span>{" "}
              {t("screens.readinessView.dimensionsScored")}{" "}
              {!allScored &&
                t("screens.readinessView.scoreEveryDimensionToEnableSignOff")}
            </p>
            {allScored && (
              <div className="grid gap-3 sm:grid-cols-2">
                {(["cro", "ops_res"] as const).map((role) => {
                  const signed =
                    role === "cro" ? a.cro_signoff_at : a.ops_res_signoff_at;
                  const name =
                    role === "cro" ? a.cro_signoff_name : a.ops_res_signoff_name;
                  const label = t(
                    `screens.readinessView.${role === "cro" ? "cro" : "opsRes"}`,
                  );
                  return (
                    <div
                      key={role}
                      data-testid={`readiness-signoff-${role}`}
                      className="rounded border border-slate-200 p-3"
                    >
                      <div className="text-xs font-semibold text-slate-700">
                        {label}
                      </div>
                      {signed ? (
                        /* B4 -- role, name, time and comment, in the
                           half-signed state too.
                
                           The approved block below has rendered all
                           four since C1. This branch -- the ceremony
                           after one signature and before the other --
                           printed "Signed by {name}" and nothing else,
                           so a signer who had just typed her reason
                           reloaded the page and found it gone. A
                           ceremony half way through is a real state
                           and the record it is building should be
                           readable in it.
                
                           The same component, not a second rendering:
                           two of these drift, and the drift is only
                           visible to whoever happens to see both. */
                        <div className="mt-1">
                          <SignatureHistory
                            testid={`readiness-signature-${role}`}
                            entries={[
                              {
                                roleLabel: label,
                                name,
                                at: signed,
                                note:
                                  (role === "cro"
                                    ? a.cro_signoff_note
                                    : a.ops_res_signoff_note) ?? null,
                              },
                            ]}
                          />
                        </div>
                      ) : holds(role) ? (
                        <div
                          data-testid={`readiness-signoff-${role}-panel`}
                          className="mt-2 space-y-2 rounded border border-brand-brass/40 bg-brand-brass/5 p-2"
                        >
                          <h3 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">
                            {t("screens.readinessView.signHeading")}
                          </h3>
                          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
                            <dt className="text-slate-500">
                              {t("screens.readinessView.summaryTier")}
                            </dt>
                            <dd className="text-slate-800">{a.set_tier ?? "—"}</dd>
                            <dt className="text-slate-500">
                              {t("screens.readinessView.summaryIndex")}
                            </dt>
                            <dd className="tabular-nums text-slate-800">
                              {a.set_index ?? "—"}
                            </dd>
                            <dt className="text-slate-500">
                              {t("screens.readinessView.summaryScored")}
                            </dt>
                            <dd className="tabular-nums text-slate-800">
                              {scored}/{r.dimensions.length}
                            </dd>
                            <dt className="text-slate-500">
                              {t("screens.readinessView.summaryFloor")}
                            </dt>
                            <dd className="text-slate-800">
                              {a.floor_applied
                                ? t("screens.readinessView.summaryFloorApplied")
                                : t("screens.readinessView.summaryFloorNot")}
                            </dd>
                          </dl>
                          <p className="text-xs text-slate-700">
                            {t("screens.readinessView.commitsTo")}
                          </p>
                          {/* C2 -- a box for the reason. This ceremony
                              and the MVB submit were the only two that
                              collected no comment at all, so two
                              records the CRO signs carried no reason
                              while eleven others did. */}
                          <label
                            className="block text-xs text-slate-600"
                            htmlFor={`readiness-note-${role}`}
                          >
                            {t("screens.readinessView.noteLabel")}
                          </label>
                          <textarea
                            dir="auto"
                            id={`readiness-note-${role}`}
                            data-testid={`readiness-signoff-${role}-note`}
                            value={signNote}
                            onChange={(e) => setSignNote(e.target.value)}
                            rows={2}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                            placeholder={t("screens.readinessView.notePlaceholder")}
                          />
                          <button
                            type="button"
                            data-testid={`readiness-signoff-${role}-btn`}
                            onClick={() => onSignoff(role, signNote)}
                            disabled={signoff.isPending}
                            className="rounded border border-brand-brassDark bg-brand-brass px-2.5 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
                          >
                            {signoff.isPending
                              ? t("screens.readinessView.signing")
                              : t("screens.readinessView.signAs", {
                                  name: signerName,
                                })}
                          </button>
                        </div>
                      ) : (
                        // A2 -- never offer a signature the API will
                        // refuse. Say who is being waited on instead.
                        <p
                          data-testid={`readiness-signoff-${role}-awaiting`}
                          className="mt-1 text-xs text-slate-500"
                        >
                          {(() => {
                            // Round 18 G23 -- the toast named Sara Al-Hassan
                            // and this line, beneath it, the seat.
                            const h = seatHolder(
                              seats.data,
                              role === "cro" ? "cro" : "head_of_opsres",
                            );
                            return h.kind === "named"
                              ? t("screens.readinessView.awaitingOtherNamed", {
                                  who: h.name,
                                  role: label,
                                })
                              : h.kind === "nobody"
                                ? t("screens.readinessView.awaitingOtherUnheld", {
                                    role: label,
                                  })
                                : t("screens.readinessView.awaitingOther", {
                                    role: label,
                                  });
                          })()}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Dimensions */}
      <section className="space-y-3">
        {r.dimensions.map((d) => {
          const setScore = a?.dimensions[d.code]?.score ?? null;
          return (
            <div
              key={d.code}
              data-testid={`readiness-dimension-${d.code}`}
              className="rounded-lg border border-slate-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="font-mono text-xs text-slate-400">{d.code}</span>{" "}
                  <span className="text-sm font-semibold text-slate-900">
                    {/* Round 9 G -- the instrument's own words, from
                        the catalogue. These were composed in
                        maturity_model.py, which made /readiness the
                        last route in the product still English under
                        the pseudo-locale. Falls back to the composed
                        English, so a key that has not landed reads as
                        English rather than as a key. */}
                    {d.name_key ? t(d.name_key, { defaultValue: d.name }) : d.name}
                  </span>
                  {/* Round 11 F3 -- a separator in the text: the row
                      read "D1 Governance, Accountability and Operating
                      Modelweight 12%" to anything taking innerText. */}
                  <span className="ms-2 text-[11px] text-slate-400">
                    <span aria-hidden="true"> · </span>
                    {t("screens.readinessView.weightPct", { pct: d.weight })}
                  </span>
                  {/* Round 16 D22 -- "suggested 4" sat beside 16.7 per
                      cent tested with nothing saying they answer
                      different questions. */}
                  {d.code === "D6" && (
                    <p
                      data-testid="readiness-d6-note"
                      className="mt-1 text-[11px] text-slate-500"
                    >
                      {t("screens.readinessView.d6Note")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    data-testid={`readiness-suggested-${d.code}`}
                    className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                  >
                    {/* Round 17 B14 -- no evidence, no suggestion. */}
                    {d.suggestion_offered === false
                      ? t("screens.readinessView.noSuggestion")
                      : `${t("screens.readinessView.suggested")} ${d.suggested_score}`}
                  </span>
                  {isDraftEditable ? (
                    <select
                      data-testid={`readiness-score-${d.code}`}
                      value={setScore ?? ""}
                      onChange={(e) =>
                        setDim.mutate({ code: d.code, score: Number(e.target.value) })
                      }
                      className="rounded border border-slate-300 px-2 py-1 text-xs"
                    >
                      <option value="">{t("screens.readinessView.set")}</option>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  ) : (
                    setScore !== null && (
                      <span className="rounded bg-brand-brass/10 px-2 py-0.5 text-[11px] font-semibold text-brand-brassDark">
                        {t("screens.readinessView.set2")} {setScore}
                      </span>
                    )
                  )}
                  <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-500">
                    {HORIZON_LABEL[d.horizon] ?? d.horizon}
                  </span>
                </div>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {d.anchor_key ? t(d.anchor_key, { defaultValue: d.anchor }) : d.anchor}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                {d.evidence_present.map((e, i) => (
                  /* Round 19 B30 -- present evidence over an estate with
                     nothing admitted is a worker's computation, not a
                     judgement: the tick is neutral until something is. */
                  <span
                    key={`p${i}`}
                    data-testid="readiness-evidence-present"
                    data-judged={r.not_suggested_reason ? "false" : "true"}
                    className={
                      r.not_suggested_reason ? "text-slate-600" : "text-emerald-700"
                    }
                  >
                    {/* The probe's own name, and the COUNT the backend
                        appended to it stays as it was: a figure is not
                        a word, and re-composing it here would need the
                        count sent separately for no gain. */}
                    ✓ {probeLabel(d.evidence_present_keys?.[i], e, t)}
                  </span>
                ))}
                {d.evidence_absent.map((e, i) => (
                  <span key={`a${i}`} className="text-rose-700">
                    ✗ {probeLabel(d.evidence_absent_keys?.[i], e, t)}
                  </span>
                ))}
                {d.external_only.map((e, i) => (
                  <span key={`x${i}`} className="text-slate-400">
                    {/* D2 -- an evidence name the bank supplied, inside
                        a translated line. Isolated with <bdi> rather
                        than <Figure>: a name takes its own direction,
                        and forcing left-to-right would turn an Arabic
                        one round. */}
                    ◦{" "}
                    <Bdi>
                      {d.external_only_keys?.[i]
                        ? t(`readiness.external.${d.external_only_keys[i]}`, {
                            defaultValue: e,
                          })
                        : e}
                    </Bdi>{" "}
                    {t("screens.readinessView.external")}
                  </span>
                ))}
              </div>
              {d.gap_route && (
                <Link
                  to={d.gap_route}
                  data-testid={`readiness-gap-${d.code}`}
                  className="mt-2 inline-block text-xs font-semibold text-brand-brassDark hover:underline"
                >
                  {t("screens.readinessView.closeThisGapRarr")}
                </Link>
              )}
            </div>
          );
        })}
      </section>
    </article>
  );
}
